import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

interface OurPlanoAndPatientResultsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title: string;
          text: string;

          alt: string;
          image_file: {
            image: {
              id: string;
              filename_download: string;
            };
          };
        };
      }[];
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",

  "text.text",

  "button.label",
  "button.href",

  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",

  "content_collection.site_content_collections_id.collection.item:images.alt",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.id",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.filename_download",
] as const;

interface OurPlanoAndPatientResultsProps {
  data?: OurPlanoAndPatientResultsData;
}

export default function OurPlanoAndPatientResults({
  data,
}: OurPlanoAndPatientResultsProps) {
  return (
    <section className="bg-white py-8 md:py-20">
      <div className="px-5 md:px-20">
        <div className="flex flex-col md:flex-row md:gap-16">
          <div className="order-1 md:order-2 mb-9 md:mb-0 grid grid-cols-2 md:grid-cols-3 gap-4 md:w-2/3 lg:w-3/5">
            {data?.content_collection?.map((collection, index) => {
              const imageItem =
                collection?.site_content_collections_id?.collection.find(
                  (item) => item.collection === "images"
                )?.item;
              const titleItem =
                collection?.site_content_collections_id?.collection.find(
                  (item) => item.collection === "site_content"
                )?.item;

              const imageId = imageItem?.image_file?.image?.id;
              const imageFilename =
                imageItem?.image_file?.image?.filename_download;
              const imageAlt = imageItem?.alt;
              const title = titleItem?.title;

              return (
                title && (
                  <Link
                    href={title?.split('"')[1] || "#"}
                    key={index}
                    className="relative aspect-[0.95] bg-[#dac79b] rounded-[0.608rem] overflow-hidden group"
                  >
                    {imageId && imageFilename && (
                      <Image
                        src={getReadableImageUrl(
                          imageId || "",
                          imageFilename || ""
                        )}
                        alt={imageAlt || ""}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 33vw"
                        quality={90}
                      />
                    )}
                    <div className="absolute inset-0 bg-white/60" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <h3
                        className="text-primary text-lg lg:text-2xl font-greycliff font-medium md:leading-[2.38rem] tracking-tight text-center px-4"
                        dangerouslySetInnerHTML={sanitizeHTML(title)}
                      />
                    </div>
                  </Link>
                )
              );
            })}
          </div>
          <div className="order-2 md:order-1 md:w-1/3 lg:w-2/5">
            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6">
              {data?.heading?.title}
            </h2>
            <div
              className="text-primary text-lg font-greycliff leading-relaxed tracking-tight mb-9 md:mb-0"
              dangerouslySetInnerHTML={{ __html: data?.text?.text || "" }}
            />
            <div className="mt-9">
              {data?.button?.href && (
                <Link href={data.button.href || "#"}>
                  <button className="w-full md:w-auto h-[4.375rem] px-9 py-[0.813rem] bg-primary text-white rounded-[3.125rem] border border-primary">
                    <span className="text-xl font-greycliff leading-7 tracking-tight">
                      {data?.button?.label}
                    </span>
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
